/**
 * AdminPatientStoriesPage — moderation queue for the `patient_stories` block.
 * Pulls stories directly from the org_site block items (status pending/approved).
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Loader2, Heart, Check, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const SLUG_OPTIONS = [
  { slug: 'abmhc', label: 'ABMHC' },
  { slug: 'bishop-manjoro', label: 'Bishop Manjoro' },
];

const AdminPatientStoriesPage = () => {
  const [slug, setSlug] = useState('abmhc');
  const qc = useQueryClient();

  // Pulls the full site, extracts patient_stories items.
  const { data: site, isLoading } = useQuery({
    queryKey: ['admin-patient-stories-site', slug],
    queryFn: () => api.get(`/sites/admin/${slug}`).then((r) => r.data),
  });

  const items = React.useMemo(() => {
    if (!site) return [];
    const block = (site.blocks || []).find((b) => b.type === 'patient_stories');
    return block?.props?.items || [];
  }, [site]);

  const approve = useMutation({
    mutationFn: (id) => api.post(`/sites/admin/${slug}/patient-story/${id}/approve`),
    onSuccess: () => {
      toast.success('Approved — story is now public');
      qc.invalidateQueries({ queryKey: ['admin-patient-stories-site', slug] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || 'Approve failed'),
  });

  const remove = useMutation({
    mutationFn: (id) => api.delete(`/sites/admin/${slug}/patient-story/${id}`),
    onSuccess: () => {
      toast.success('Deleted');
      qc.invalidateQueries({ queryKey: ['admin-patient-stories-site', slug] });
    },
  });

  const buckets = {
    pending: items.filter((s) => (s.status || 'pending') === 'pending'),
    approved: items.filter((s) => s.status === 'approved'),
  };

  const card = (s) => (
    <Card key={s.id} className="mb-3" data-testid={`story-card-${s.id}`}>
      <CardContent className="pt-4 pb-3 space-y-2">
        <div className="flex items-start gap-3 flex-wrap">
          {s.photo_url && <img src={s.photo_url} alt={s.name} className="w-12 h-12 rounded-full object-cover" />}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-sm">{s.name}</span>
              {s.title && <span className="text-xs text-slate-500">· {s.title}</span>}
              <Badge variant={s.status === 'approved' ? 'default' : 'destructive'}>{s.status || 'pending'}</Badge>
            </div>
            <p className="text-xs text-slate-500">{(s.submitted_at || s.date || '').slice(0, 16).replace('T', ' ')}</p>
          </div>
        </div>
        <p className="text-sm text-slate-700 whitespace-pre-line">{s.message}</p>
        <div className="flex flex-wrap gap-2 pt-2 border-t">
          {s.status !== 'approved' && (
            <Button size="sm" onClick={() => approve.mutate(s.id)} data-testid={`story-approve-${s.id}`}>
              <Check className="h-3.5 w-3.5 mr-1" /> Approve & publish
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="text-red-600"
            onClick={() => window.confirm('Delete this story?') && remove.mutate(s.id)}
            data-testid={`story-delete-${s.id}`}
          >
            <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="admin-patient-stories-page">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Heart className="h-5 w-5 text-slate-500" />
          Patient Stories Moderation
        </h1>
        <div className="flex gap-2">
          {SLUG_OPTIONS.map((opt) => (
            <Button
              key={opt.slug}
              size="sm"
              variant={slug === opt.slug ? 'default' : 'outline'}
              onClick={() => setSlug(opt.slug)}
              data-testid={`story-slug-${opt.slug}`}
            >
              {opt.label}
            </Button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="py-20 text-center text-slate-500">
          <Loader2 className="h-6 w-6 animate-spin inline mr-2" /> Loading…
        </div>
      ) : (
        <Tabs defaultValue="pending">
          <TabsList>
            <TabsTrigger value="pending">Pending {buckets.pending.length > 0 && `(${buckets.pending.length})`}</TabsTrigger>
            <TabsTrigger value="approved">Approved ({buckets.approved.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="pending" className="pt-4">
            {buckets.pending.length === 0 ? (
              <Card><CardContent className="py-8 text-center text-sm text-slate-500">No pending stories.</CardContent></Card>
            ) : buckets.pending.map(card)}
          </TabsContent>
          <TabsContent value="approved" className="pt-4">
            {buckets.approved.length === 0 ? (
              <Card><CardContent className="py-8 text-center text-sm text-slate-500">No approved stories yet.</CardContent></Card>
            ) : buckets.approved.map(card)}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default AdminPatientStoriesPage;
